/**
 * Which single pane a narrow screen shows, and the browser history that makes
 * the system back button close it.
 *
 * # The pane is derived, never stored
 *
 * There used to be a `mobilePane` signal in `app.tsx` that the list and the
 * reader wrote to as they were clicked. It was a second answer to a question
 * the data already answers, and two answers drift: archiving the last message
 * from the reading pane cleared `openEmail` without telling `mobilePane`, so a
 * phone was left looking at an empty reader with no way back that did not
 * involve guessing. {@link pane} is computed from the same signals the desktop
 * layout reads, so a pane cannot be showing something that is not open.
 *
 * `folderNav` is the one bit that is genuinely not in the data: nothing about
 * `scope` says whether someone is *choosing* a mailbox or has already chosen
 * one. It cannot contradict the data because there is no data for it to
 * contradict, and it is cleared the moment a choice is made.
 *
 * # Back has to work, and there is no router
 *
 * A phone user presses the system back button. If that leaves the site instead
 * of closing the message, the app is broken -- so the layers stacked above the
 * message list are mirrored into `history` as entries, and `popstate` unwinds
 * them.
 *
 * The mirroring is one-way and reconciled rather than commanded, which is what
 * makes it survive the parts of the app that know nothing about it. `u` and
 * `Esc` still call `store.closeEmail()` directly, the reader's Back button
 * still calls the same thing, and an archive that closes the last message
 * closes it too; every one of those ends in a state change, and the effect
 * below notices that there is one entry too many and drops it. Nothing in the
 * app has to remember to route.
 *
 * # One URL, and it is the settings page
 *
 * Two things are written to the address bar, and both earn it. Everything else
 * calls `pushState` with the URL already showing, because there is nothing to
 * put in it -- which pane of the mail you are looking at is a property of the
 * window, not of a place.
 *
 * # A message has an address
 *
 * `#/message/<account>/<id>`, and this used to say the opposite: that the app
 * had no per-message URL and that inventing one meant inventing a parser too.
 * The parser is real and it is below; what changed is that three separate
 * things now need to name a message and were each going to invent their own way
 * of doing it -- a notification the OS hands back when someone taps it, a
 * message opened in a window of its own, and a reload of a client that had one
 * open. One address serves all three, and the alternative was three.
 *
 * **The ids are the server's.** The desktop build keeps a local replica whose
 * rows are numbered differently, and the two have been confused before; a URL
 * is JMAP id space, and translation happens at the Rust boundary and nowhere
 * else. A link carrying a local row id would resolve to a different message on
 * one build and to nothing on the other, which is the worst shape a bug can
 * have.
 *
 * The id is opaque and useless without the session that can resolve it, which
 * is what makes this safe to put where a URL goes. It is not secret, though:
 * anyone reading the address bar over a shoulder learns that *some* message is
 * open, and the same is true of every webmail client there has ever been.
 *
 * # Settings has one too
 *
 * Which settings page is open is a
 * choice someone made about the app rather than about one message, it survives
 * being sent to somebody else ("it is under Privacy"), and losing it to a
 * reload in the middle of changing a password is the kind of small betrayal
 * that teaches people not to reload. So `#/settings/<page>` is written, read
 * back at startup, and kept in step with {@link settingsPage} from then on.
 *
 * It is a fragment rather than a path because the daemon serves this app from
 * one route and a real path would have to exist on the server as well. It is
 * only read at startup: the hash is an output of the state everywhere else, and
 * a module that also adopted it mid-session would have two answers to the same
 * question and a loop between them.
 *
 * The fragment is preserved by the `replaceState` in `lib/accounts.ts`, which
 * strips the OAuth return parameters at import time -- it runs first, and this
 * module then adopts whatever URL it left.
 */

import { computed, effect, signal } from "@preact/signals";

import * as compose from "./compose";
import type { EmailAddress } from "./types";
import * as store from "./store";

export type Pane = "sidebar" | "list" | "reader" | "panel";

/**
 * The width at which the sidebar, the list and the reader are all on screen.
 *
 * Named here and matched in `styles.css`. The two have to agree: this is what
 * the app asks before it moves focus into a pane, and moving focus on a layout
 * where nothing was replaced is how a wide window loses the reader's place.
 */
export const THREE_COLUMN = "(min-width: 56.25rem)";

/** True while a narrow screen is showing the mailbox list. */
export const folderNav = signal(false);

/** The keyboard-shortcut sheet. Here rather than in `app.tsx` so that back closes it. */
export const helpOpen = signal(false);

/**
 * The recipient-suggestions manager, reached from Settings.
 *
 * Here for the same reason as {@link helpOpen}, and it needs the reason more:
 * it is a full-screen sheet on a phone, opened from a screen that is itself a
 * layer, and a back press with it up has to close the sheet rather than jump
 * out of Settings and leave it hanging over the mail.
 */
export const suggestionsOpen = signal(false);

/**
 * The width at which the settings group list and the selected page are both on
 * screen, rather than one at a time.
 *
 * Lower than {@link THREE_COLUMN}, and not by accident: below 56.25rem the
 * settings panel is the whole window, so there is more room for two columns
 * inside it at 48rem than there is at 900px, where the sidebar has taken 200px
 * of the window before the panel starts. Matched in `styles.css`, and the two
 * have to agree -- this is what decides whether the selected page is a layer
 * the back button has to walk out of, and a stylesheet that showed both columns
 * while this said otherwise would leave a back press doing nothing visible.
 */
export const SETTINGS_SPLIT = "(min-width: 48rem)";

/** True while the settings group list and a page are on screen together. */
export const settingsSplit = signal(window.matchMedia(SETTINGS_SPLIT).matches);

/**
 * The pages of the settings shell.
 *
 * These strings are written into the address bar, so they are part of the app's
 * surface: renaming one breaks a link somebody saved or sent. `signin` is spelt
 * without punctuation for that reason and not because the screen is called
 * that.
 */
export type SettingsPage =
  | "accounts"
  | "appearance"
  | "sending"
  | "notifications"
  | "lowpriority"
  | "offline"
  | "privacy"
  | "signin"
  | "about";

const SETTINGS_PAGES: readonly SettingsPage[] = [
  "accounts",
  "appearance",
  "sending",
  "notifications",
  "lowpriority",
  "offline",
  "privacy",
  "signin",
  "about",
];

/**
 * Which settings page is open, or `null` for the group list with none chosen.
 *
 * `null` is only reachable on a narrow screen, where the group list is a pane
 * of its own: {@link settingsSplit} fills it in as soon as both columns are on
 * screen, because a two-column settings screen with an empty right-hand column
 * is a layout announcing that it is broken.
 *
 * It is deliberately not cleared on the way out of settings. Coming back to the
 * page you were last on is what the address bar has been saying all along, and
 * a signal that reset itself would contradict the URL it is written to.
 */
export const settingsPage = signal<SettingsPage | null>(null);

/**
 * Where the gear lands someone who has never chosen a page.
 *
 * The first entry in the group list, which is Accounts -- the one thing on this
 * screen that is about their mail rather than about this browser, and the one
 * people arrive looking for.
 */
const FIRST_PAGE: SettingsPage = "accounts";

/**
 * The pane a single-pane layout shows.
 *
 * Order matters: a view that is not mail wins over the folder list, so that
 * tapping Settings from the folder pane goes to Settings rather than appearing
 * to do nothing.
 */
export const pane = computed<Pane>(() => {
  if (store.view.value !== "mail") return "panel";
  if (folderNav.value) return "sidebar";
  if (store.openEmailId.value !== null) return "reader";
  return "list";
});

/**
 * Everything stacked above the message list, outermost first.
 *
 * The list is the floor of the app -- there is nothing to go back to from it --
 * so it is not a layer. Compose and the two sheets are, on every width: a
 * full-screen composer that the back button walks out of, taking the tab with
 * it, is the same bug as the reader one. The settings page is the one entry
 * here that depends on the width, because it is the one that is sometimes a
 * pane of its own and sometimes a column beside the thing it would go back to.
 */
type Layer = Pane | "settings-page" | "compose" | "suggestions" | "help";

const layers = computed<Layer[]>(() => {
  const out: Layer[] = [];
  const p = pane.value;
  if (p !== "list") out.push(p);
  // Only where it replaced the group list. Two columns of settings are one
  // screen and one layer, so back leaves settings altogether from a wide
  // window -- which is what a back press means when nothing was covered up.
  if (p === "panel" && !settingsSplit.value && settingsPage.value !== null) {
    out.push("settings-page");
  }
  // Not in a window that is only a composer. A layer is something stacked over
  // the message list, and there is no message list here -- so a back press that
  // "closed" it would leave the window holding nothing, which is the same bug
  // the standalone reader has already been fixed for twice.
  if (store.composeOpen.value && composeSolo.value === null) out.push("compose");
  if (suggestionsOpen.value) out.push("suggestions");
  if (helpOpen.value) out.push("help");
  return out;
});

/** Leave the folder pane, whatever was chosen in it. */
export function leaveFolders(): void {
  folderNav.value = false;
}

/** Close the topmost layer, as the back button would. */
export function back(): void {
  const stack = layers.peek();
  const top = stack[stack.length - 1];
  if (top) close(top);
}

function close(layer: Layer): void {
  switch (layer) {
    case "help":
      helpOpen.value = false;
      break;
    case "suggestions":
      suggestionsOpen.value = false;
      break;
    case "compose":
      // May refuse: a draft that failed to save keeps the window open. The
      // reconcile below puts the history entry back when that happens.
      void compose.closeAfterSaving();
      break;
    case "settings-page":
      // Back to the group list, still inside settings. The page underneath is
      // not unmounted by this -- see `ui/SettingsShell.tsx` -- so a half-typed
      // password is still there when it is chosen again.
      settingsPage.value = null;
      break;
    case "reader":
      store.closeEmail();
      break;
    case "panel":
      store.view.value = "mail";
      break;
    case "sidebar":
      folderNav.value = false;
      break;
    case "list":
      break;
  }
}

// ---- the history mirror ------------------------------------------------------

/** How many entries this module has pushed. */
let depth = 0;
/** True while a `go()` we asked for has not come back yet. */
let settling = false;
/**
 * How many layers are open, for the one caller outside this module that needs
 * to know: Android's back gesture, which has to answer "did I consume that".
 *
 * A count rather than the stack, because that is the whole question, and
 * exporting `layers` would invite somebody to branch on which layer is on top --
 * a decision this module makes and should keep making.
 */
export function layerDepth(): number {
  return layers.peek().length;
}

/**
 * Answer Android's back gesture. True means the app consumed it.
 *
 * `history.back()` rather than reaching into {@link close}, because history
 * *is* the layer model here -- `sync()` below pushes an entry per layer and
 * `popstate` unwinds them. Tauri's own handler could not use this: it asks the
 * WebView's `canGoBack()`, which does not count same-document entries, so it
 * exited the app instead of closing a layer.
 *
 * **At the floor, back reveals the sidebar, and it never stops doing that.** A
 * phone shows one pane, so from the message list the accounts and folders are
 * the thing that is off screen and the only place back could sensibly go.
 * Setting {@link folderNav} makes a layer, so this needs no history call of its
 * own: the effect below pushes the entry, and the next back comes through the
 * branch above and closes it -- landing back at the message list, where the
 * next press reveals the pane again. The two trade places forever.
 *
 * **So back cannot leave the app, and that is the requirement rather than a
 * side effect.** There was a one-shot flag here that spent the offer after one
 * reveal, so the third press exited; Amit asked for it gone in those words --
 * "I want it to loop forever, I don't want it to exit". Anyone reading this and
 * reaching for the flag again should take it up with him, not with the code:
 * Home and the recents switcher still leave, and the platform convention this
 * breaks is one he has weighed and rejected.
 *
 * Not where the sidebar is already on screen -- there is nothing to reveal, so
 * consuming the press would make back do nothing visible at all, which is the
 * one outcome worse than either of the two above. That is a three-column
 * window, which no phone in one pane ever is.
 */
export function androidBack(): boolean {
  const stack = layers.peek();
  if (stack.length > 0) {
    history.back();
    return true;
  }
  // At the floor, and the floor has a door back to the folder pane rather than
  // out of the app. Amit's call, stated twice and stated plainly: back cycles
  // between the message list and the folder pane and never leaves.
  if (window.matchMedia(THREE_COLUMN).matches) return false;
  folderNav.value = true;
  return true;
}

/** True while `popstate` is closing layers, so the effect does not fight it. */
let unwinding = false;

/**
 * Bring the history depth and the address bar back in line with the layers.
 *
 * Depth first, then the URL, and never the other way round: the push below
 * carries whatever URL the browser is already showing, and the fragment is then
 * written onto the entry it just made. Doing it in one `pushState` would mean
 * teaching the depth loop what the URL is, and doing it before would put the
 * new page's fragment on the entry someone is about to go back to.
 */
function sync(): void {
  if (settling || unwinding) return;
  reconcileDepth();
  reconcileUrl();
}

function reconcileDepth(): void {
  const wanted = layers.peek().length;
  if (wanted > depth) {
    while (depth < wanted) {
      depth += 1;
      history.pushState({ hmDepth: depth }, "");
    }
  } else if (wanted < depth) {
    const delta = wanted - depth;
    depth = wanted;
    settling = true;
    history.go(delta);
  }
}

/** The address this state should be showing. See the fragment note at the top. */
function wantedUrl(): string {
  const here = `${location.pathname}${location.search}`;
  // A window that is only a composer keeps saying so, for the reason the
  // standalone reader does: this address is what the window would be rebuilt
  // from after a reload, and a composer that came back as the whole mail client
  // would have quietly lost the reply somebody was part-way through. Held as
  // the fragment it was loaded at rather than recomputed, because the draft
  // moves as it is typed and its address does not.
  const composing = composeSolo.peek();
  if (composing !== null) return `${here}${composing}`;
  if (store.view.peek() === "mail") {
    const open = store.openEmailId.peek();
    if (open === null) return here;
    // Split on the first colon rather than the last: `keyOf` joins an account
    // id and a message id with one, and RFC 8620 restricts both to
    // `A-Za-z0-9_-`, so the first colon is the only colon.
    const cut = open.indexOf(":");
    if (cut < 1) return here;
    const address = { account: open.slice(0, cut), email: open.slice(cut + 1) };
    // A standalone page keeps saying so. Writing the ordinary fragment here
    // would mean a reload, or a copied link, silently turning one message into
    // the whole client -- and the address bar is the only place a browser can
    // keep that fact.
    return `${here}${solo.peek() ? soloFragmentFor(address) : fragmentFor(address)}`;
  }
  const page = settingsPage.peek();
  return `${here}#/settings${page === null ? "" : `/${page}`}`;
}

function reconcileUrl(): void {
  const wanted = wantedUrl();
  if (`${location.pathname}${location.search}${location.hash}` === wanted) return;
  // `replaceState` rather than a push of its own: which settings page is open
  // is a property of the entry that is already there. Pushing here would put a
  // history entry between every pair of settings pages on a wide window, so
  // leaving settings would take one back press per page anyone had looked at.
  history.replaceState({ hmDepth: depth }, "", wanted);
}

/**
 * Take the settings page out of the address bar, once, at startup.
 *
 * Silent about anything else in the URL, including a fragment that names no
 * page we know: someone following a link written against a later version of
 * this app should land on settings rather than on an error, and the group list
 * is the honest answer to "which page?" when the name means nothing here.
 */
function adoptUrl(): void {
  if (location.hash.startsWith("#/message/") || location.hash.startsWith("#/read/")) {
    const named = messageFromLink(location.hash);
    if (named) pendingMessage.value = named;
    // Only when the address parsed. A `#/read/` naming nothing valid is not a
    // standalone anything -- it would be a page with no message and no way to
    // reach one, which is worse than the ordinary client showing an inbox.
    if (named && location.hash.startsWith("#/read/")) solo.value = true;
    return;
  }

  if (location.hash.startsWith("#/compose/")) {
    const asked = composeFromLink(location.hash);
    // Same rule as `#/read/` above, and it is load-bearing in the same way: a
    // page that is only a composer and could not work out which composer has
    // nothing to fall back to. Left unset, it renders as the ordinary client,
    // which is a worse answer than a wrong composer but a much better one than
    // an empty window.
    if (asked) {
      pendingCompose.value = asked;
      composeSolo.value = composeFragmentFor(asked);
    }
    return;
  }

  const match = /^#\/settings(?:\/([a-z-]+))?$/.exec(location.hash);
  if (!match) return;
  store.view.value = "settings";
  const named = match[1];
  if (named && (SETTINGS_PAGES as readonly string[]).includes(named)) {
    settingsPage.value = named as SettingsPage;
  }
}

/**
 * The fragment naming a message: what the address bar shows, and what a row
 * links to.
 *
 * One function for both because they must never disagree. The address bar is
 * written from the open message and a row's `href` from the row, and a copy of
 * this that encoded differently would produce links that resolve to nothing on
 * exactly the ids that needed encoding.
 *
 * Encoded despite RFC 8620 restricting ids to `A-Za-z0-9_-`: this is a URL, and
 * trusting a charset to hold forever is how a parser acquires an injection.
 */
export function fragmentFor(address: { account: string; email: string }): string {
  return `#/message/${encodeURIComponent(address.account)}/${encodeURIComponent(address.email)}`;
}

/**
 * The same message, addressed as a page that is *only* that message.
 *
 * What a double-click opens in a browser. `#/message/...` means "open this in
 * the app" -- the sidebar, the list, the reading pane, with that message in
 * front -- and it is what a notification and a pasted link should do, because
 * somebody arriving from outside wants their mail and not a dead end. `#/read/`
 * means the message and nothing around it, which is what the desktop gets from
 * a detached window and what the browser should get from a new tab.
 *
 * Two routes rather than one route with a flag, because the difference has to
 * survive a reload and a copied link: the address bar is the only place this
 * fact can live in a browser, so it is written into the path rather than into
 * something the page holds.
 *
 * Same encoding as {@link fragmentFor} and parsed by the same function, so the
 * two spellings cannot disagree about what an id looks like.
 */
export function soloFragmentFor(address: { account: string; email: string }): string {
  return `#/read/${encodeURIComponent(address.account)}/${encodeURIComponent(address.email)}`;
}

/**
 * Whether this page is one message rather than the app.
 *
 * Set once, at startup, from the address it was loaded at, and never again --
 * so navigating inside the page cannot turn a standalone reader back into the
 * full client, and `reconcileUrl` keeps writing the `#/read/` form for as long
 * as the tab lives.
 *
 * Deliberately not folded into `windowRole()` from the transport. That answers
 * "what kind of window did the platform give me", which a browser cannot know
 * and cannot change; this answers "what was this page asked to show", which is
 * a URL fact. Overloading one signal with the other is precisely the bug that
 * put a tab strip in the browser, and the fix there was to stop inferring one
 * capability from a neighbouring one.
 */
export const solo = signal(false);

/**
 * The address this page was loaded at, when what it was asked to show is a
 * composer and nothing else. `null` everywhere else.
 *
 * The composer's counterpart to {@link solo}, and set the same way: once, at
 * startup, from the URL. It holds the fragment rather than a boolean because it
 * has a second job -- `wantedUrl` writes it back, so the window keeps its
 * address across the reloads and the state changes that would otherwise
 * overwrite the fragment with the mail client's idea of where it is.
 *
 * Distinct from `windowRole() === "compose"` for the reason {@link solo} is
 * distinct from `windowRole() === "message"`: that answers what kind of window
 * the platform handed this page, which a browser cannot know, and this answers
 * what the page was asked to show, which is a fact about the URL. On the
 * desktop both are true of a compose window and neither is inferred from the
 * other.
 */
export const composeSolo = signal<string | null>(null);

/**
 * The message named by a link, in either of the two spellings it arrives in.
 *
 * `#/message/<account>/<id>` is what this app writes to its own address bar.
 * `hydramail://message/<account>/<id>` is what the operating system hands back
 * when somebody taps a notification, and the two carry the same two segments on
 * purpose — one address, two spellings, so there is no second thing to keep in
 * step when either end changes.
 *
 * **This is the only parser.** The desktop side deliberately forwards the raw
 * string rather than picking it apart in Rust: the ids have to be checked
 * against RFC 8620's charset before they reach the daemon, and the same rule
 * written in two languages is a rule that will hold in one of them. What
 * arrives here is untrusted in the ordinary way — it came from an address bar
 * or from the OS — so it is validated rather than trusted, and the seven
 * refusal cases in `address.test.ts` are what that means.
 */
export function messageFromLink(link: string): { account: string; email: string } | null {
  // Spelled out rather than made compact with an optional slash: `#//message`
  // and `hydramail:/message` are not addresses this app writes, and a parser
  // that quietly accepts near-misses is one that will accept a near-miss
  // somebody constructed.
  const match = /^(?:#\/message|#\/read|hydramail:\/\/message)\/([^/]+)\/([^/]+)$/.exec(
    link.trim(),
  );
  if (!match) return null;
  const account = decodeId(match[1]);
  const email = decodeId(match[2]);
  // Both or neither. Half an address names no message, and opening the
  // account's inbox instead would be a different request than the one made.
  if (account === null || email === null) return null;
  return { account, email };
}

/** What a `mailto:` link asks for: who to write to, and a start on the text. */
export interface Mailto {
  to: EmailAddress[];
  cc: EmailAddress[];
  bcc: EmailAddress[];
  subject: string;
  body: string;
}

/**
 * Parse a `mailto:` URL the OS handed us into composer fields, or null.
 *
 * The other half of "HydraMail is your email app": the OS routes a clicked
 * `mailto:` here (registered in `tauri.conf.json`, forwarded raw by
 * `deeplink.rs` and by the Android share intent), and this turns it into a new
 * message. Recipients come from both the path (`mailto:a@b,c@d`) and a `to=`
 * query field; `cc`, `bcc`, `subject` and `body` from the query. Addresses
 * arrive bare, so each becomes an `EmailAddress` with no display name.
 *
 * Anything that is not `mailto:` is refused so the caller can fall through to
 * `messageFromLink`. A bare `mailto:` with no address is accepted -- it opens a
 * blank composer, which is what clicking "email" with no target means.
 */
export function mailtoFromLink(link: string): Mailto | null {
  const raw = link.trim();
  if (!/^mailto:/iu.test(raw)) return null;
  const rest = raw.slice(raw.indexOf(":") + 1);
  const q = rest.indexOf("?");
  const path = q >= 0 ? rest.slice(0, q) : rest;
  const params = new URLSearchParams(q >= 0 ? rest.slice(q + 1) : "");

  const decode = (v: string): string => {
    try {
      return decodeURIComponent(v.replace(/\+/gu, " "));
    } catch {
      return v;
    }
  };
  const addrs = (list: string): EmailAddress[] =>
    list
      .split(",")
      .map((a) => decode(a).trim())
      .filter((a) => a.length > 0)
      .map((email) => ({ name: null, email }));

  return {
    to: [...addrs(path), ...addrs(params.get("to") ?? "")],
    cc: addrs(params.get("cc") ?? ""),
    bcc: addrs(params.get("bcc") ?? ""),
    subject: decode(params.get("subject") ?? ""),
    body: decode(params.get("body") ?? ""),
  };
}

/**
 * One id out of a URL, or null if it is not one.
 *
 * Checked against RFC 8620's charset rather than merely decoded, because this
 * string is about to be put in a request to the daemon and everything upstream
 * of here is somebody else's typing: an address bar, a link in a message, a
 * payload handed back by the operating system. A daemon that would have
 * rejected it anyway is not a reason to forward it -- the point is that this
 * client never asks a question it knows to be malformed.
 *
 * The length cap is the RFC's. Nothing legitimate approaches it.
 */
function decodeId(raw: string | undefined): string | null {
  if (raw === undefined) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // A stray `%` in a hand-edited URL.
    return null;
  }
  return /^[A-Za-z0-9_-]{1,255}$/.test(decoded) ? decoded : null;
}

/**
 * A message named by the URL this app was loaded on, waiting for a session.
 *
 * Read once and cleared, by `app.tsx`, when there is somewhere to open it --
 * the same shape as the pending notification it sits beside, and for the same
 * reason: an address can be adopted before there is any account to resolve it
 * against, and the answer to that is to remember rather than to guess.
 */
export const pendingMessage = signal<{ account: string; email: string } | null>(null);

/** Consume it, if there is one. */
export function takePendingMessage(): { account: string; email: string } | null {
  const held = pendingMessage.peek();
  if (held) pendingMessage.value = null;
  return held;
}

/**
 * A `mailto:` that arrived before there was a session to open a composer over.
 *
 * The same park-and-spend as {@link pendingMessage}: a cold launch via the
 * email-app association reaches the app before it has signed in, and the
 * composer needs an account, so the request waits here and the effect in
 * `app.tsx` spends it once there is one.
 */
export const pendingMailto = signal<Mailto | null>(null);

/** Consume it, if there is one. */
export function takePendingMailto(): Mailto | null {
  const held = pendingMailto.peek();
  if (held) pendingMailto.value = null;
  return held;
}

// ---- composing somewhere else --------------------------------------------------

/**
 * Everything the composer needs to be opened in a webview that has none of it.
 *
 * A compose window is a **separate JS context**: it does not share the signals,
 * the store or the mirror with the window that asked for it, so every parameter
 * has to survive being written into a URL and read back by a process that was
 * not running when the button was pressed. That is the whole reason this type
 * exists rather than a call into `lib/compose.ts` -- there is nothing on the
 * other side of a `WebviewWindowBuilder` to call.
 *
 * `address` is the message being answered: the parent of a reply, the original
 * of a forward, or the draft being resumed. `null` for a blank message, and the
 * two are checked against `kind` rather than merely carried -- a reply to
 * nothing is not a reply, and a blank message with a parent is a spelling
 * nothing in this app produces.
 *
 * `to` is prefill and nothing more, which is why it is a list of raw strings
 * rather than parsed addresses: it is what somebody typed or what a `mailto:`
 * carried, and it goes through `compose.parseAddress` at the far end like any
 * other recipient. It is deliberately *not* an id -- see {@link decodeId} for
 * the charset the two halves of `address` are held to, and note that no such
 * rule can apply here, because an email address is not an id and the daemon is
 * never asked to resolve one.
 */
export interface ComposeRequest {
  kind: compose.ComposeKind;
  address: { account: string; email: string } | null;
  to: string[];
}

/**
 * The five, spelled once.
 *
 * Written into URLs, so these strings are part of the app's surface in exactly
 * the way {@link SettingsPage}'s are, and renaming one breaks a window somebody
 * has open across a restart.
 */
const COMPOSE_KINDS: readonly compose.ComposeKind[] = [
  "new",
  "reply",
  "replyAll",
  "forward",
  "draft",
];

/**
 * How many recipients a link may prefill.
 *
 * A bound rather than a limit anybody will reach: this string becomes a window
 * URL, and a URL with no ceiling is a way to make one that no platform will
 * open and that fails without saying why.
 */
const MAX_PREFILL = 100;

/**
 * The address of a composer: what a compose window is loaded at.
 *
 * The same arrangement as {@link fragmentFor} and for the same reason -- one
 * function writes it and {@link composeFromLink} reads it, so the two cannot
 * come to disagree about how a kind is spelled or where the recipients go.
 *
 * The recipients are a query *inside the fragment*, which is where they have to
 * be: everything after the `#` is the fragment, so `index.html#/compose/new?to=`
 * carries them without the daemon ever seeing a query string, and an address is
 * not something the RFC 8620 charset can hold in a path segment anyway.
 */
export function composeFragmentFor(request: ComposeRequest): string {
  const at = request.address
    ? `/${encodeURIComponent(request.address.account)}/${encodeURIComponent(request.address.email)}`
    : "";
  const prefill = request.to.map((a) => `to=${encodeURIComponent(a)}`).join("&");
  return `#/compose/${request.kind}${at}${prefill ? `?${prefill}` : ""}`;
}

/**
 * What distinguishes one compose window from another, or `null` for one that is
 * distinguished by nothing.
 *
 * The desktop turns this into a window label and focuses an existing window
 * rather than opening a second one -- which is the rule detached message
 * windows already follow, and it matters more here: two windows editing one
 * draft are two autosaves racing to `replacing` the same revision, and the loser
 * leaves a copy behind. Asking to reply to a message twice should land in the
 * reply already being written.
 *
 * `null` for a blank message, deliberately. Two blank messages are two
 * messages, and a person writing one while another waits is the case
 * multiple compose windows exist for. Nothing distinguishes them because there
 * is nothing yet to distinguish.
 *
 * Only ids reach this, and only in the shapes {@link decodeId} permits, which
 * is what keeps a label inside the glob `capabilities/default.json` grants. See
 * `src-tauri/src/windows.rs` for what a label outside it silently loses.
 */
export function composeIdentityFor(request: ComposeRequest): string | null {
  const at = request.address;
  return at ? `${request.kind}:${at.account}:${at.email}` : null;
}

/**
 * The composer a link asks for, or `null` if it does not ask for one.
 *
 * The read side of {@link composeFragmentFor}, held to the same standard as
 * {@link messageFromLink}: this string came out of an address bar or off a
 * window that was loaded before any of this code ran, so it is validated rather
 * than trusted, and a near-miss is refused rather than interpreted.
 *
 * Only the fragment spelling. There is no `hydramail://compose` because nothing
 * produces one -- the OS hands this app links naming messages, and a `mailto:`
 * is not routed here yet (see `src-tauri/src/links.rs`). A branch with no caller
 * is a branch nothing keeps honest.
 */
export function composeFromLink(link: string): ComposeRequest | null {
  // Spelled out rather than made compact, for the reason `messageFromLink` is:
  // the near-misses this refuses are the point of writing it this way.
  const match = /^#\/compose\/([A-Za-z]+)(?:\/([^/?]+)\/([^/?]+))?(?:\?([^#]*))?$/u.exec(
    link.trim(),
  );
  if (!match) return null;

  const kind = match[1] as compose.ComposeKind;
  if (!COMPOSE_KINDS.includes(kind)) return null;

  // A parent and a kind that cannot have one, or a kind that must have one and
  // does not. Both are refused rather than repaired: opening a blank composer
  // when a reply was asked for loses the quoting, the recipients and the
  // threading headers, and does it silently.
  const wantsAddress = kind !== "new";
  const hasAddress = match[2] !== undefined;
  if (wantsAddress !== hasAddress) return null;

  let address: { account: string; email: string } | null = null;
  if (hasAddress) {
    const account = decodeId(match[2]);
    const email = decodeId(match[3]);
    // Both or neither, as above: half an address names no message.
    if (account === null || email === null) return null;
    address = { account, email };
  }

  const to = prefillFrom(match[4]);
  if (to === null) return null;
  return { kind, address, to };
}

/**
 * The `to=` values out of a compose fragment's query, or `null` if it holds
 * anything else.
 *
 * Strict about the key as well as the values. A fragment carrying `cc=` or
 * `subject=` is a link written against a grammar this app does not have, and
 * dropping the part it does not understand would open a composer that silently
 * lost half of what was asked for -- which is the failure mode a prefill is
 * least able to survive, because nobody checks a field they did not type.
 */
function prefillFrom(query: string | undefined): string[] | null {
  if (query === undefined || query === "") return [];
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(query);
  } catch {
    return null;
  }
  const out: string[] = [];
  for (const [key, value] of params) {
    if (key !== "to") return null;
    // Parsed rather than merely non-empty. This is going into a From/To field
    // somebody will press Send under, and `parseAddress` is the one thing in
    // this client that decides what an address is.
    if (compose.parseAddress(value) === null) return null;
    out.push(value);
  }
  return out.length > MAX_PREFILL ? null : out;
}

/**
 * A composer named by the URL this page was loaded on, waiting for a session.
 *
 * The same shape as {@link pendingMessage} and spent by the same effect in
 * `app.tsx`, because it has the same problem: a compose window is loaded at its
 * address, and that address names an account and a message the store cannot
 * resolve until there is a session to resolve them against.
 */
export const pendingCompose = signal<ComposeRequest | null>(null);

/** Consume it, if there is one. */
export function takePendingCompose(): ComposeRequest | null {
  const held = pendingCompose.peek();
  if (held) pendingCompose.value = null;
  return held;
}

function onPop(event: PopStateEvent): void {
  const state = event.state as { hmDepth?: number } | null;
  const target = Math.max(0, Math.trunc(state?.hmDepth ?? 0));

  if (settling) {
    // Our own `go()` landing. The app is already where it wants to be; adopt
    // the depth and re-check, in case something moved while it was in flight.
    settling = false;
    depth = target;
    sync();
    return;
  }

  const stack = layers.peek();
  depth = target;

  if (target < stack.length) {
    // Read the layers to close before closing any of them. Compose saves its
    // draft before it goes, so `layers` does not shrink synchronously, and a
    // loop that re-read it would close the pane underneath as well.
    unwinding = true;
    try {
      for (let i = stack.length - 1; i >= target; i -= 1) {
        const layer = stack[i];
        if (layer) close(layer);
      }
    } finally {
      unwinding = false;
    }
  } else if (target > stack.length) {
    // Forward, into a pane whose contents we did not record and cannot rebuild.
    // Rewriting this entry rather than pushing keeps the depth honest; the
    // press does nothing, which is better than a back button that then lies.
    depth = stack.length;
    history.replaceState({ hmDepth: depth }, "");
  }

  sync();
}

/**
 * Start mirroring layers into history. Called once, from `main.tsx`.
 *
 * The first `replaceState` labels the entry the app was loaded on, so a `back`
 * from the first pushed layer arrives somewhere this module recognises rather
 * than at a `null` state it has to guess about.
 */
export function watchHistory(): void {
  // Before the first `replaceState`, so the entry the app loaded on is labelled
  // with the state the URL asked for rather than with the state it displaced.
  adoptUrl();
  history.replaceState({ hmDepth: 0 }, "");
  window.addEventListener("popstate", onPop);

  // "Accounts" is an older spelling of "settings, on the accounts page", and it
  // is still what the sidebar, the empty mailbox and the Google return path all
  // say. Normalised here rather than at each of those, because a view that is
  // sometimes `accounts` and sometimes `settings` for the same screen is two
  // answers to one question, and the pane, the history and the address bar all
  // ask it.
  effect(() => {
    if (store.view.value === "accounts") {
      settingsPage.value = "accounts";
      store.view.value = "settings";
    }
  });

  effect(() => {
    // Read through the computed so the effect re-runs on any layer change, and
    // read the two the address bar depends on: which page is open moves no
    // layer at all on a wide window, and the URL still has to follow it.
    layers.value;
    store.view.value;
    settingsPage.value;
    sync();
  });

  // A window that grows past the three-column breakpoint has a sidebar again,
  // and a `folderNav` left set would keep a layer -- and a back press that
  // does nothing visible -- alive behind it.
  const wide = window.matchMedia(THREE_COLUMN);
  const settle = () => {
    if (wide.matches) folderNav.value = false;
  };
  wide.addEventListener("change", settle);
  settle();

  // The same shape of problem one breakpoint down: a window wide enough for
  // both settings columns must not be showing an empty one, and the group list
  // stops being a layer the moment it is permanently on screen.
  const split = window.matchMedia(SETTINGS_SPLIT);
  const settleSettings = () => {
    settingsSplit.value = split.matches;
    if (split.matches && settingsPage.value === null) settingsPage.value = FIRST_PAGE;
  };
  split.addEventListener("change", settleSettings);
  settleSettings();
}
